import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { parseAssetDefs, withAssetDefs } from './assetDefs';

import type { CodeoBrand } from 'js/src/client';
import { type PlanManifest, retimeMomentsFromSpans } from 'js/src/agent/codeo/plan-manifest';
import { BLANK_COMPOSITION } from 'js/src/agent/codeo/starterCompositions';
import {
    type ParsedTranscript,
    parseTranscriptsFromHtml,
    withTranscripts,
} from 'js/src/agent/codeo/voiceover-transcript';
import type { WebsiteAnalysisLogEntry } from 'js/src/agent/codeo/website-brand/types';

const LOCAL_STORAGE_KEY = 'codeo:state:v1';

export interface SelectedElement {
    label: string;
    tagName: string;
    outerHtml: string;
}

export interface PendingReferenceVideo {
    fileName: string;
    status: 'attached' | 'analyzing' | 'error';
    durationS?: number;
    cutCount?: number;
    errorMessage?: string;
}

export interface ReferenceAnalysisFrame {
    timeS: number;
    dataUrl: string;
}

export interface ReferenceAnalysisToolCall {
    timeS: number;
    spanS: number;
    count: number;
    returnedFrames: ReferenceAnalysisFrame[];
}

export interface ReferenceAnalysisTranscript {
    words: Array<{ w: string; s: number; e: number }>;
    languageCode: string;
    hasVoiceover: boolean;
}

export interface ReferenceAnalysisLogEntry {
    id: string;
    fileName: string;
    timestamp: number;
    durationS: number;
    cutCount: number;
    palette: string[];
    cutTimestampsS: number[];
    representativeFrames: ReferenceAnalysisFrame[];
    toolCalls: ReferenceAnalysisToolCall[];
    transcript: ReferenceAnalysisTranscript | null;
    analysis: unknown;
    usagePromptTokens: number;
    usageCompletionTokens: number;
    usageTotalTokens: number;
}

export interface PendingWebsite {
    url: string;
    status: 'attached' | 'analyzing' | 'error';
    errorMessage?: string;
}

export interface CodeoState {
    currentHtml: string;
    assets: Record<string, string>; // assetId -> data URI; baked into currentHtml as CSS vars
    // The element with visible direct-manipulation handles on the canvas.
    // Short-lived: cleared on background click, outside-iframe click,
    // scrub, agent rewrite. Single-asset.
    domEditSelection: SelectedElement | null;
    // The asset attached to the next chat message. Lives longer than
    // `domEditSelection` — survives canvas focus loss so the user can
    // click into the textarea without losing context.
    chatReferenceAsset: SelectedElement | null;
    // Bumped on each agent-driven rewrite; gates iframe remounts so user
    // direct edits (which mutate the iframe DOM in place) don't reload it.
    agentRevision: number;
    // Mirrored from useRootAgent; suspends the picker while the agent is
    // mid-turn so a rewrite can't land under an in-flight gesture.
    isAgentProcessing: boolean;
    // The plan committed by commit_plan. Session-only working state, not markup
    // and not persisted — read by get_video and the layout-measurement probes
    // for the duration of the authoring run, then gone on reload.
    committedPlan: PlanManifest | null;
    // Voiceover transcripts keyed by audio element id. System-owned, like
    // `assets`: every commit bakes a transcript block in after its matching
    // audio element (and nowhere else) — the agent never authors or sees them.
    transcripts: Record<string, ParsedTranscript>;
    pendingReference: PendingReferenceVideo | null;
    lastReferenceAnalysis: ReferenceAnalysisLogEntry | null;
    pendingWebsite: PendingWebsite | null;
    lastWebsiteAnalysis: WebsiteAnalysisLogEntry | null;
    // Onboarding handoff: a first user message that ChatPane sends on mount to kick
    // off the conversation with the brand + content the user selected in onboarding.
    queuedInitialMessage: string | null;
    brands: CodeoBrand[];
    selectedBrandId: number | null;
    isLoadingBrands: boolean;
}

function loadInitial(): CodeoState {
    if (typeof window === 'undefined') {
        return defaultState();
    }
    try {
        const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw) return defaultState();
        const parsed = JSON.parse(raw) as Partial<CodeoState>;
        const currentHtml =
            typeof parsed.currentHtml === 'string' ? parsed.currentHtml : BLANK_COMPOSITION;
        return {
            currentHtml,
            // Re-derive the asset registry from the html's defs block.
            assets: parseAssetDefs(currentHtml),
            domEditSelection: null,
            chatReferenceAsset: null,
            agentRevision: 0,
            isAgentProcessing: false,
            committedPlan: null,
            transcripts: parseTranscriptsFromHtml(currentHtml),
            pendingReference: null,
            lastReferenceAnalysis: null,
            pendingWebsite: null,
            lastWebsiteAnalysis: null,
            queuedInitialMessage: null,
            brands: [],
            selectedBrandId: null,
            isLoadingBrands: false,
        };
    } catch {
        return defaultState();
    }
}

function defaultState(): CodeoState {
    return {
        currentHtml: BLANK_COMPOSITION,
        assets: {},
        domEditSelection: null,
        chatReferenceAsset: null,
        agentRevision: 0,
        isAgentProcessing: false,
        committedPlan: null,
        transcripts: {},
        pendingReference: null,
        lastReferenceAnalysis: null,
        pendingWebsite: null,
        lastWebsiteAnalysis: null,
        queuedInitialMessage: null,
        brands: [],
        selectedBrandId: null,
        isLoadingBrands: false,
    };
}

const codeoSlice = createSlice({
    name: 'codeo',
    initialState: loadInitial(),
    reducers: {
        // Agent rewrite. Clears selection + chip because the elements
        // they pointed at may not exist in the new composition.
        setCurrentHtml(state, action: PayloadAction<{ html: string }>) {
            // Keep the system-owned asset defs block current: the agent authors markup that
            // references var(--asset-<id>) but never the block itself (get_video elides it).
            state.currentHtml = withTranscripts(
                withAssetDefs(action.payload.html, state.assets),
                state.transcripts,
            );
            state.agentRevision += 1;
            state.domEditSelection = null;
            state.chatReferenceAsset = null;
        },
        // User direct-manipulation edit. Skips `agentRevision++` — the
        // live gesture has already updated the iframe DOM.
        applyUserEdit(state, action: PayloadAction<{ html: string }>) {
            state.currentHtml = withTranscripts(
                withAssetDefs(action.payload.html, state.assets),
                state.transcripts,
            );
        },
        registerAssets(state, action: PayloadAction<{ assets: Record<string, string> }>) {
            state.assets = { ...state.assets, ...action.payload.assets };
            state.currentHtml = withAssetDefs(state.currentHtml, state.assets);
        },
        resetComposition(state) {
            state.currentHtml = BLANK_COMPOSITION;
            state.assets = {};
            state.domEditSelection = null;
            state.chatReferenceAsset = null;
            state.agentRevision += 1;
            state.committedPlan = null;
            state.transcripts = {};
            state.pendingReference = null;
            state.lastReferenceAnalysis = null;
            state.pendingWebsite = null;
            state.lastWebsiteAnalysis = null;
            state.queuedInitialMessage = null;
        },
        commitPlan(state, action: PayloadAction<{ plan: PlanManifest }>) {
            state.committedPlan = action.payload.plan;
        },
        // Snap the committed plan's estimated moment times to the voiceover's
        // real spans (dispatched by create_voiceover) so layout probes and
        // finding attribution follow the composition's actual clock.
        retimePlanMoments(
            state,
            action: PayloadAction<{
                spans: Array<{ label: string; start: number; end: number }>;
                audioDurationS: number;
            }>,
        ) {
            if (!state.committedPlan) return;
            const retimed = retimeMomentsFromSpans(
                state.committedPlan.moments,
                action.payload.spans,
                action.payload.audioDurationS,
            );
            if (retimed) {
                state.committedPlan = { ...state.committedPlan, moments: retimed };
            }
        },
        registerTranscript(
            state,
            action: PayloadAction<{ audioId: string; transcript: ParsedTranscript }>,
        ) {
            state.transcripts[action.payload.audioId] = action.payload.transcript;
            state.currentHtml = withTranscripts(state.currentHtml, state.transcripts);
        },
        // One click on the canvas raises both intents — the "two facets
        // of one intent" diverge later through their own dismiss paths.
        selectAsset(state, action: PayloadAction<SelectedElement>) {
            state.domEditSelection = action.payload;
            state.chatReferenceAsset = action.payload;
        },
        clearDomEditSelection(state) {
            state.domEditSelection = null;
        },
        updateSelectionCapture(state, action: PayloadAction<SelectedElement>) {
            const payload = action.payload;
            if (state.domEditSelection && state.domEditSelection.label === payload.label) {
                state.domEditSelection = payload;
            }
            if (state.chatReferenceAsset && state.chatReferenceAsset.label === payload.label) {
                state.chatReferenceAsset = payload;
            }
        },
        clearChatReferenceAsset(state) {
            state.chatReferenceAsset = null;
        },
        setIsAgentProcessing(state, action: PayloadAction<boolean>) {
            state.isAgentProcessing = action.payload;
        },
        setPendingReference(state, action: PayloadAction<PendingReferenceVideo>) {
            state.pendingReference = action.payload;
        },
        clearPendingReference(state) {
            state.pendingReference = null;
        },
        setLastReferenceAnalysis(state, action: PayloadAction<ReferenceAnalysisLogEntry>) {
            state.lastReferenceAnalysis = action.payload;
        },
        clearLastReferenceAnalysis(state) {
            state.lastReferenceAnalysis = null;
        },
        setPendingWebsite(state, action: PayloadAction<PendingWebsite>) {
            state.pendingWebsite = action.payload;
        },
        clearPendingWebsite(state) {
            state.pendingWebsite = null;
        },
        setLastWebsiteAnalysis(state, action: PayloadAction<WebsiteAnalysisLogEntry>) {
            state.lastWebsiteAnalysis = action.payload;
        },
        clearLastWebsiteAnalysis(state) {
            state.lastWebsiteAnalysis = null;
        },
        setQueuedInitialMessage(state, action: PayloadAction<string>) {
            state.queuedInitialMessage = action.payload;
        },
        clearQueuedInitialMessage(state) {
            state.queuedInitialMessage = null;
        },
        setCodeoBrands(state, action: PayloadAction<CodeoBrand[]>) {
            state.brands = action.payload;
        },
        setIsLoadingBrands(state, action: PayloadAction<boolean>) {
            state.isLoadingBrands = action.payload;
        },
        setSelectedBrandId(state, action: PayloadAction<number | null>) {
            state.selectedBrandId = action.payload;
        },
    },
});

export const {
    setCurrentHtml,
    applyUserEdit,
    registerAssets,
    resetComposition,
    selectAsset,
    clearDomEditSelection,
    updateSelectionCapture,
    clearChatReferenceAsset,
    setIsAgentProcessing,
    commitPlan,
    retimePlanMoments,
    registerTranscript,
    setPendingReference,
    clearPendingReference,
    setLastReferenceAnalysis,
    clearLastReferenceAnalysis,
    setPendingWebsite,
    clearPendingWebsite,
    setLastWebsiteAnalysis,
    clearLastWebsiteAnalysis,
    setQueuedInitialMessage,
    clearQueuedInitialMessage,
    setCodeoBrands,
    setIsLoadingBrands,
    setSelectedBrandId,
} = codeoSlice.actions;
export const codeoLocalStorageKey = LOCAL_STORAGE_KEY;
export default codeoSlice;
